import './styles.scss';
import React, {useState} from 'react';
import {Form, Input, Popover, Switch} from "antd";
import {UserInfoFormTypes} from "./User.info.form.types";
import AlternateEmailsFormList from "../Users.alternateEmailsFormList/Users.alternateEmailsFormList";
import GeographicalStatesSelect from "../../common/Select.geographicalStates.tsx/Select.geographicalStates";
import {ROLES} from "../../../settings";
import Button from "../../common/Button/Button";
import {AlertOutlined, BankOutlined, QuestionCircleOutlined, SaveOutlined, UserOutlined} from "@ant-design/icons";
import {IUser} from "../../../api/User/User.types";
import FormSectionHeader from "../../common/FormSectionHeader/FormSectionHeader";
import Keywords from "../Keywords/Keywords";
import NAICSTags from "../NAICS.tags/NAICS.tags";
import PSCTags from "../PSC.tags/PSC.tags";
import FormLabel from "../../common/Form.label/Form.label";
import useUserStore from "../../../data/User.store";
import AccountStatusSelect from "../Users.accountStatus/User.accountStatus.select";

interface Props {
    user: IUser
    asAcceptInvitation?: boolean,
    onSubmit?: (values: any) => void,
}

function UsersInfoForm({user, asAcceptInvitation, onSubmit}: Props) {
    const {user: storeUser} = useUserStore();
    const [submittingForm, setSubmittingForm] = useState(false);
    const [form] = Form.useForm();

    //eslint-disable-next-line
    const [valuesForm, setValuesForm] = useState({});

    if (!user) {
        return null;
    }

    function normalizeFields(fields: string[], values: any) {
        let ret = {};
        fields.forEach(field => {
            if (values[field] !== undefined) {
                if (values[field] === "") {
                    ret[field] = null;
                } else if (Array.isArray(values[field])) {
                    ret[field] = values[field].join(",");
                } else {
                    ret[field] = field;
                }
            }
        });
        return {...values, ...ret};
    }

    async function onSubmitClick() {
        if (submittingForm) return false;
        setSubmittingForm(true);
        try {
            let valid = await form.validateFields();
            if (!valid.errorFields && onSubmit) {
                let values = form.getFieldsValue();

                let normalized = normalizeFields([
                    UserInfoFormTypes.AlternateEmails,
                    UserInfoFormTypes.StatesOfBusiness,
                    UserInfoFormTypes.NaicsCodes,
                    UserInfoFormTypes.PscCodes,
                    UserInfoFormTypes.Keywords
                ], values);

                await onSubmit(normalized);
            }
        } catch (e) {

        }
        setSubmittingForm(false);
    }

    const initialValues = {
        [UserInfoFormTypes.DunsNumber]: user?.company?.duns_number,
        [UserInfoFormTypes.CompanyName]: user?.company?.name,
        [UserInfoFormTypes.Email]: user.email,
        [UserInfoFormTypes.AllowAlertEmails]: user.allow_alert_emails,
        [UserInfoFormTypes.AccountStatus]: user.account_status,
        [UserInfoFormTypes.Active]: user.active,
    }
    if (user?.company?.states_of_business) {
        initialValues[UserInfoFormTypes.StatesOfBusiness] = user.company.states_of_business.split(",");
    }
    if (user?.alternate_emails) {
        initialValues[UserInfoFormTypes.AlternateEmails] = user.alternate_emails.split(",");
        user.alternate_emails.split(",").forEach((email, index) => {
            form.setFieldsValue({[index]: email});
        })
    }
    if (user.company) {
        const {states_of_business, duns_number, name, keywords, naics_codes, psc_codes} = user.company;
        initialValues[UserInfoFormTypes.DunsNumber] = duns_number;
        initialValues[UserInfoFormTypes.CompanyName] = name;

        if (states_of_business) {
            initialValues[UserInfoFormTypes.StatesOfBusiness] = states_of_business.split(",");
        }
        initialValues[UserInfoFormTypes.Keywords] = keywords ? keywords.split(",") : [];
        initialValues[UserInfoFormTypes.PscCodes] = psc_codes ? psc_codes.split(",") : [];
        initialValues[UserInfoFormTypes.NaicsCodes] = naics_codes ? naics_codes.split(",") : [];
    }


    function onValuesChange(v) {
        setValuesForm(v)
    }

    return (
        <Form className='users-info-form' form={form} initialValues={initialValues}
                onValuesChange={onValuesChange}>
            <FormSectionHeader text='User' icon={<UserOutlined/>}/>
            <div className='single-form-item'>
                <FormLabel title={<><Popover title="Why can't I change my email?" overlayStyle={{maxWidth: 300}}
                                                content="Once your account is created, you can't change your email. If there is an error, please contact a FAMR representative to assist you.">
                    <QuestionCircleOutlined style={{marginRight: 6}}/>
                </Popover>
                    Email</>}/>
                <div className='form-header'>
                    <div className='title'>

                    </div>
                </div>
                <div className='form-content'>
                    <Form.Item name={UserInfoFormTypes.Email}>
                        <Input size='large' disabled/>
                    </Form.Item>
                </div>
            </div>
            <div className='single-form-item'>
                <FormLabel title='Alternate Emails'
                            subtitle='Alternate emails will also be sent new opportunity emails'/>
                <div className='form-content'>
                    <AlternateEmailsFormList name={UserInfoFormTypes.AlternateEmails} form={form}/>
                </div>
            </div>
            {storeUser?.role?.name === ROLES.ADMIN &&
            <>
                <div className='single-form-item'>
                    <FormLabel title='Account Status'/>
                    <div className='form-content'>
                        <Form.Item name={UserInfoFormTypes.AccountStatus}>
                            <AccountStatusSelect/>
                        </Form.Item>
                    </div>
                </div>
                <div className='single-form-item'>
                    <FormLabel title='Active'/>
                    <div className='form-content'>
                        <Form.Item name={UserInfoFormTypes.Active} valuePropName="checked">
                            <Switch/>
                        </Form.Item>
                    </div>
                </div>
            </>}
            <FormSectionHeader text='Company' icon={<BankOutlined/>}/>
            <div className='single-form-item'>
                <FormLabel title='DUNS Number'/>
                <div className='form-content'>
                    <Form.Item name={UserInfoFormTypes.DunsNumber}>
                        <Input size='large'/>
                    </Form.Item>
                </div>
            </div>
            <div className='single-form-item'>
                <FormLabel title='Company Name'/>
                <div className='form-content'>
                    <Form.Item name={UserInfoFormTypes.CompanyName}>
                        <Input size='large'/>
                    </Form.Item>
                </div>
            </div>
            <div className='single-form-item'>
                <FormLabel title='States of Business'/>
                <div className='form-content'>
                    <Form.Item name={UserInfoFormTypes.StatesOfBusiness}>
                        <GeographicalStatesSelect disabled={storeUser?.role?.name === ROLES.CLIENT}/>
                    </Form.Item>
                </div>
            </div>
            {asAcceptInvitation &&
            <div className='single-form-item'>
                <FormLabel title='Allow Alert Emails'/>
                <div className='form-content'>
                    <Form.Item name={UserInfoFormTypes.AllowAlertEmails} valuePropName="checked">
                        <Switch/>
                    </Form.Item>
                </div>
            </div>}

            {!asAcceptInvitation &&
            <>
                <FormSectionHeader text='Alerts' help='Global alerts settings that narrow down alert notifications.'
                                    icon={<AlertOutlined/>}/>
                <div className='single-form-item'>
                    <FormLabel title='Allow Alert Emails'/>
                    <div className='form-content'>
                        <Form.Item name={UserInfoFormTypes.AllowAlertEmails} valuePropName="checked">
                            <Switch/>
                        </Form.Item>
                    </div>
                </div>
                <div className='single-form-item'>
                    <FormLabel title='NAICS Codes'/>
                    <div className='form-content'>
                        <Form.Item name={UserInfoFormTypes.NaicsCodes}>
                            <NAICSTags/>
                        </Form.Item>
                    </div>
                </div>
                <div className='single-form-item'>
                    <FormLabel title='PSC Codes'/>
                    <div className='form-content'>
                        <Form.Item name={UserInfoFormTypes.PscCodes}>
                            <PSCTags/>
                        </Form.Item>
                    </div>
                </div>
                <div className='single-form-item'>
                    <FormLabel title='Keywords'/>
                    <div className='form-content'>
                        <Form.Item name={UserInfoFormTypes.Keywords}>
                            <Keywords/>
                        </Form.Item>
                    </div>
                </div>
            </>}
            <Form.Item>
                <Button onClick={onSubmitClick} htmlType='submit'
                        enabled={asAcceptInvitation ? true : form.isFieldsTouched()}
                        icon={<SaveOutlined/>} iconPosition='left' loading={submittingForm}>
                    {asAcceptInvitation ? "Submit Information" : "Save Changes"}
                </Button>
            </Form.Item>
        </Form>
    )

}

export default UsersInfoForm;